import type { ID, Offset } from '@octopus-do/octopus-project-core'

import { NOTE_ORIGIN, NOTE_TARGET, NOTE_TARGET_CLASS } from './constants'

const contains = (rect: DOMRect, { x, y }: Offset) =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom

// Hit-testing by geometry, not elementsFromPoint: a note is drawn inside the
// page it hangs on, so the page would be "under" the note wherever it was
// dropped. Pages sit inside their section, so the innermost match wins.
export const findNoteTarget = (point: Offset): ID | null => {
  const hits = Array.from(
    document.querySelectorAll<HTMLElement>(`[${NOTE_TARGET}]`)
  ).filter((el) => contains(el.getBoundingClientRect(), point))

  const innermost = hits.filter(
    (el) => !hits.some((other) => other !== el && el.contains(other))
  )

  return innermost.at(-1)?.getAttribute(NOTE_TARGET) ?? null
}

const findOrigin = (parentId: ID) =>
  document.querySelector(`[${NOTE_ORIGIN}="${parentId}"]`)

// Where a note whose top left corner is at `screen` sits on `parentId`.
export const toParentOffset = (
  screen: Offset,
  parentId: ID,
  scale: number
): Offset | null => {
  const origin = findOrigin(parentId)?.getBoundingClientRect()

  if (!origin) {
    return null
  }

  return {
    x: Math.round((screen.x - origin.left) / scale),
    y: Math.round((screen.y - origin.top) / scale),
  }
}

// The inverse: where a note at `offset` on `parentId` is on screen now.
export const toScreen = (
  offset: Offset,
  parentId: ID,
  scale: number
): Offset | null => {
  const origin = findOrigin(parentId)?.getBoundingClientRect()

  if (!origin) {
    return null
  }

  return {
    x: origin.left + offset.x * scale,
    y: origin.top + offset.y * scale,
  }
}

const findTarget = (id: ID) =>
  document.querySelector(`[${NOTE_TARGET}="${id}"]`)

// Moves the drop-target highlight from one page or section to another.
export const markNoteTarget = (prev: ID | null, next: ID | null): void => {
  if (prev) {
    findTarget(prev)?.classList.remove(NOTE_TARGET_CLASS)
  }

  if (next) {
    findTarget(next)?.classList.add(NOTE_TARGET_CLASS)
  }
}
